(function () {
  class CollectionScrollSlider {
    constructor(root) {
      this.root = root;
      this.section = root.closest('.csss-section');
      if (!this.section) return;

      this.stage = root.querySelector('.csss-stage');
      this.track = (this.stage || root).querySelector('.csss-track');
      this.progressBar = this.section.querySelector('.csss-progress__bar');
      this.prevBtn = this.section.querySelector('.csss-nav--prev');
      this.nextBtn = this.section.querySelector('.csss-nav--next');
      this.cards = Array.from(root.querySelectorAll('.csss-card:not(.csss-card--clone)'));
      this.slideIndex = 0;
      this.displayIndex = 0;
      this.currentGap = null;
      this.raf = null;
      this.mobileRafLoop = null;
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      this.maxGap = parseFloat(getComputedStyle(this.section).getPropertyValue('--csss-gap-max')) || 24;
      this.autoplayEnabled = root.dataset.autoplay === 'true';
      this.disableAutoplayMobile = root.dataset.disableAutoplayMobile === 'true';
      this.autoplaySpeed = (parseFloat(root.dataset.autoplaySpeed) || 4) * 1000;
      this.autoplayLoop = root.dataset.autoplayLoop !== 'false';
      this.perView = parseInt(root.dataset.perView, 10) || 3;
      this.perViewTablet = parseInt(root.dataset.perViewTablet, 10) || 3;
      this.perViewMobile = parseInt(root.dataset.perViewMobile, 10) || 1;
      this.autoplayTimer = null;

      this.continuousSetting = root.dataset.continuous === 'true';
      this.isContinuousMode = false;
      this.continuousSpeed = Math.max(parseFloat(root.dataset.continuousSpeed) || 600, 80);
      this.continuousDirection = root.dataset.continuousDirection === 'right' ? 'right' : 'left';
      this.resizeTimer = null;
      this.touchBound = false;
      this.sectionWidthPx = 0;
      this.minStageWidth = 76;
      this.maxStageWidth = 100;
      this.minStageHeight = 76;
      this.maxStageHeight = 100;
      this.currentStageWidth = 76;
      this.targetStageWidth = 76;
      this.currentStageHeight = 76;
      this.targetStageHeight = 76;
      this.mobileGap = 0;

      this.onScroll = this.onScroll.bind(this);
      this.onResize = this.onResize.bind(this);
      this.onTouchStart = this.onTouchStart.bind(this);
      this.onTouchEnd = this.onTouchEnd.bind(this);
      this.mobileLoop = this.mobileLoop.bind(this);
      this.touchStartX = 0;

      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onResize, { passive: true });

      this.bindTouchSwipe();

      root.addEventListener('mouseenter', () => {
        if (this.isContinuousMode) {
          this.setMarqueePaused(true);
        } else {
          this.pauseAutoplay();
        }
      });
      root.addEventListener('mouseleave', () => {
        if (this.isContinuousMode) {
          if (this.shouldRunAutoScroll()) {
            this.setMarqueePaused(false);
          }
        } else {
          this.startAutoplay();
        }
      });

      this.prevBtn?.addEventListener('click', () => {
        this.slide(-1);
        this.restartAutoplay();
      });
      this.nextBtn?.addEventListener('click', () => {
        this.slide(1);
        this.restartAutoplay();
      });

      document.addEventListener('visibilitychange', () => {
        if (this.isContinuousMode) {
          this.setMarqueePaused(document.hidden || !this.shouldRunAutoScroll());
          return;
        }
        if (document.hidden) {
          this.pauseAutoplay();
        } else {
          this.startAutoplay();
        }
      });

      this.syncPresentationMode();
      this.onResize();
      this.onScroll();

      if (!this.isContinuousMode) {
        this.startAutoplay();
      } else {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => this.applyMarqueeAnimation());
        });
      }
    }

    bindTouchSwipe() {
      if (this.touchBound || this.isContinuousMode) return;
      this.root.addEventListener('touchstart', this.onTouchStart, { passive: true });
      this.root.addEventListener('touchend', this.onTouchEnd, { passive: true });
      this.touchBound = true;
    }

    unbindTouchSwipe() {
      if (!this.touchBound) return;
      this.root.removeEventListener('touchstart', this.onTouchStart);
      this.root.removeEventListener('touchend', this.onTouchEnd);
      this.touchBound = false;
    }

    wantsContinuousMode() {
      return this.continuousSetting && this.shouldRunAutoScroll() && !this.reducedMotion;
    }

    syncPresentationMode() {
      const shouldBeContinuous = this.wantsContinuousMode();

      if (shouldBeContinuous && !this.isContinuousMode) {
        this.setupContinuous();
      } else if (!shouldBeContinuous && this.isContinuousMode) {
        this.teardownContinuous();
      } else if (!shouldBeContinuous) {
        this.bindTouchSwipe();
        if (this.prevBtn) this.prevBtn.style.display = '';
        if (this.nextBtn) this.nextBtn.style.display = '';
      }
    }

    setupContinuous() {
      if (this.cards.length === 0 || this.isContinuousMode) return;

      this.stopMobileLoop();
      this.unbindTouchSwipe();

      const fragment = document.createDocumentFragment();
      this.cards.forEach((card) => {
        const clone = card.cloneNode(true);
        clone.setAttribute('aria-hidden', 'true');
        clone.classList.add('csss-card--clone');
        fragment.appendChild(clone);
      });
      this.track.appendChild(fragment);

      this.root.classList.add('csss-slider--continuous');
      this.root.classList.remove('csss-slider--mobile-expanding');
      this.track.classList.add('csss-track--continuous');
      this.track.style.transition = 'none';
      this.track.style.setProperty('--csss-slide-offset', '0px');
      this.setGap(this.maxGap);
      this.isContinuousMode = true;

      if (this.prevBtn) this.prevBtn.style.display = 'none';
      if (this.nextBtn) this.nextBtn.style.display = 'none';
    }

    teardownContinuous() {
      if (!this.isContinuousMode) return;

      this.track.querySelectorAll('.csss-card--clone').forEach((clone) => clone.remove());
      this.root.classList.remove('csss-slider--continuous');
      this.track.classList.remove('csss-track--continuous');
      this.track.style.animationPlayState = '';
      this.track.style.transition = '';
      delete this.track.dataset.marqueeDirection;
      this.track.style.removeProperty('--csss-marquee-duration');
      this.isContinuousMode = false;
      this.slideIndex = 0;
      this.displayIndex = 0;
      this.currentGap = null;

      if (this.prevBtn) this.prevBtn.style.display = '';
      if (this.nextBtn) this.nextBtn.style.display = '';
      this.bindTouchSwipe();
    }

    getSetWidth() {
      const firstClone = this.track.querySelector('.csss-card--clone');
      if (firstClone) return firstClone.offsetLeft;

      const gap = parseFloat(getComputedStyle(this.track).getPropertyValue('--csss-card-gap')) || this.maxGap;
      const cardWidth = this.cards[0]?.getBoundingClientRect().width || 0;
      return (cardWidth + gap) * this.cards.length;
    }

    applyMarqueeAnimation() {
      if (!this.isContinuousMode || !this.cards.length) return;

      const setWidth = this.getSetWidth();
      if (setWidth <= 0) return;

      const duration = setWidth / this.continuousSpeed;
      this.track.style.setProperty('--csss-marquee-duration', `${duration}s`);
      this.track.dataset.marqueeDirection = this.continuousDirection;
    }

    setMarqueePaused(paused) {
      if (!this.isContinuousMode || !this.track) return;
      this.track.style.animationPlayState = paused ? 'paused' : 'running';
    }

    isMobileViewport() {
      return window.innerWidth <= 767;
    }

    isMobileExpandingMode() {
      return this.isMobileViewport() && !this.isContinuousMode;
    }

    syncMobileExpandingMode() {
      const active = this.isMobileExpandingMode();
      this.root.classList.toggle('csss-slider--mobile-expanding', active);
      if (active) {
        this.readMobileStageLimits();
        this.sliderWidthPx = this.root.getBoundingClientRect().width;
        this.mobileGap = 0;
        this.setGap(0);
        if (this.currentStageWidth < this.minStageWidth) {
          this.currentStageWidth = this.minStageWidth;
          this.currentStageHeight = this.minStageHeight;
        }
        this.displayIndex = this.slideIndex;
        this.applyMobileDimensions();
        this.applyMobileOffset();
      } else {
        this.stopMobileLoop();
        this.displayIndex = this.slideIndex;
      }
    }

    readMobileStageLimits() {
      const styles = getComputedStyle(this.section);
      this.minStageWidth =
        parseFloat(styles.getPropertyValue('--csss-mobile-stage-min')) || 76;
      this.maxStageWidth =
        parseFloat(styles.getPropertyValue('--csss-mobile-stage-max')) || 100;
      this.minStageHeight = this.minStageWidth;
      this.maxStageHeight = this.maxStageWidth;
    }

    updateMobileStageTarget(progress) {
      if (this.reducedMotion) {
        this.targetStageWidth = this.maxStageWidth;
        this.targetStageHeight = this.maxStageHeight;
        return;
      }
      const intensity = Math.sin(progress * Math.PI);
      this.targetStageWidth =
        this.minStageWidth + (this.maxStageWidth - this.minStageWidth) * intensity;
      this.targetStageHeight =
        this.minStageHeight + (this.maxStageHeight - this.minStageHeight) * intensity;
    }

    startMobileLoop() {
      if (!this.isMobileExpandingMode() || this.mobileRafLoop) return;
      this.mobileRafLoop = requestAnimationFrame(this.mobileLoop);
    }

    stopMobileLoop() {
      if (this.mobileRafLoop) {
        cancelAnimationFrame(this.mobileRafLoop);
        this.mobileRafLoop = null;
      }
    }

    mobileLoop() {
      let needsMore = false;

      const widthDiff = this.targetStageWidth - this.currentStageWidth;
      const heightDiff = this.targetStageHeight - this.currentStageHeight;

      if (Math.abs(widthDiff) > 0.05) {
        this.currentStageWidth += widthDiff * 0.1;
        needsMore = true;
      } else {
        this.currentStageWidth = this.targetStageWidth;
      }

      if (Math.abs(heightDiff) > 0.05) {
        this.currentStageHeight += heightDiff * 0.1;
        needsMore = true;
      } else {
        this.currentStageHeight = this.targetStageHeight;
      }

      this.applyMobileDimensions();
      this.applyMobileOffset();

      if (needsMore) {
        this.mobileRafLoop = requestAnimationFrame(this.mobileLoop);
      } else {
        this.mobileRafLoop = null;
      }
    }

    getMobileStageWidthPx() {
      const sliderWidth = this.root.getBoundingClientRect().width;
      return (sliderWidth * this.currentStageWidth) / 100;
    }

    applyMobileDimensions() {
      this.section.style.setProperty('--csss-current-width', `${this.currentStageWidth.toFixed(3)}%`);
      this.section.style.setProperty('--csss-current-height', `${this.currentStageHeight.toFixed(3)}%`);
      const stagePx = this.getMobileStageWidthPx();
      this.section.style.setProperty('--csss-stage-width', `${stagePx.toFixed(2)}px`);
    }

    applyMobileOffset() {
      const stagePx = this.getMobileStageWidthPx();
      const offset = -this.slideIndex * stagePx;

      this.track.style.transition = 'none';
      this.track.style.setProperty('--csss-slide-offset', `${offset.toFixed(2)}px`);
    }

    shouldRunAutoScroll() {
      if (this.disableAutoplayMobile && this.isMobileViewport()) return false;
      return true;
    }

    startAutoplay() {
      if (
        !this.autoplayEnabled ||
        !this.shouldRunAutoScroll() ||
        this.reducedMotion ||
        this.isContinuousMode ||
        this.isMobileExpandingMode()
      ) {
        return;
      }
      if (this.cards.length <= this.getVisibleCount()) return;
      this.pauseAutoplay();
      this.autoplayTimer = setInterval(() => this.autoAdvance(), this.autoplaySpeed);
    }

    pauseAutoplay() {
      if (this.autoplayTimer) {
        clearInterval(this.autoplayTimer);
        this.autoplayTimer = null;
      }
    }

    restartAutoplay() {
      if (this.autoplayEnabled && !this.isContinuousMode && this.shouldRunAutoScroll()) {
        this.startAutoplay();
      }
    }

    autoAdvance() {
      if (!this.autoplayLoop) {
        this.pauseAutoplay();
        return;
      }
      this.slide(1);
    }

    getMaxSlideIndex() {
      if (!this.cards.length) return 0;
      if (this.isMobileExpandingMode()) return Math.max(0, this.cards.length - 1);
      const visible = this.getVisibleCount();
      return Math.max(0, this.cards.length - visible);
    }

    normalizeSlideIndex() {
      const count = this.cards.length;
      if (!count) {
        this.slideIndex = 0;
        return;
      }
      const maxIndex = this.getMaxSlideIndex();
      if (this.slideIndex > maxIndex) this.slideIndex = 0;
      if (this.slideIndex < 0) this.slideIndex = maxIndex;
    }

    onScroll() {
      if (this.raf) return;
      this.raf = requestAnimationFrame(() => {
        this.updateGap();
        this.raf = null;
      });
    }

    onResize() {
      this.syncPresentationMode();
      this.syncMobileExpandingMode();

      const visible = this.getVisibleCount();
      this.section.style.setProperty('--csss-visible', visible);

      if (this.isContinuousMode) {
        this.setGap(this.maxGap);
        const sliderWidth = this.root.getBoundingClientRect().width;
        this.section.style.setProperty('--csss-slider-width', `${sliderWidth}px`);
        clearTimeout(this.resizeTimer);
        this.resizeTimer = setTimeout(() => this.applyMarqueeAnimation(), 120);
        if (this.progressBar) this.progressBar.style.width = '100%';
        if (!document.hidden) {
          this.setMarqueePaused(false);
        }
        return;
      }

      if (this.isMobileExpandingMode()) {
        this.sliderWidthPx = this.root.getBoundingClientRect().width;
        this.readMobileStageLimits();
        this.updateGap();
        this.applyMobileDimensions();
        this.applyMobileOffset();
        this.updateNavState();
        return;
      }

      if (this.shouldRunAutoScroll()) {
        this.startAutoplay();
      } else {
        this.pauseAutoplay();
      }

      this.normalizeSlideIndex();
      this.updateSlide(false);
      this.updateGap();
      this.updateNavState();
    }

    onTouchStart(event) {
      this.touchStartX = event.changedTouches[0].screenX;
    }

    onTouchEnd(event) {
      const delta = event.changedTouches[0].screenX - this.touchStartX;
      if (Math.abs(delta) < 40) return;
      this.slide(delta < 0 ? 1 : -1);
    }

    updateGap() {
      const rect = this.section.getBoundingClientRect();
      const viewport = window.innerHeight;
      const sectionHeight = rect.height;

      const totalDistance = viewport + sectionHeight;
      const traveled = viewport - rect.top;
      const progress = Math.min(1, Math.max(0, traveled / totalDistance));

      this.syncMobileExpandingMode();

      if (this.isMobileExpandingMode()) {
        this.updateMobileStageTarget(progress);
        this.startMobileLoop();

        if (this.progressBar) {
          this.progressBar.style.width = `${progress * 100}%`;
        }
        return;
      }

      let targetGap = this.maxGap;
      if (!this.reducedMotion) {
        targetGap = this.maxGap * Math.abs(Math.cos(progress * Math.PI));
        if (this.currentGap === null) this.currentGap = targetGap;
        this.currentGap += (targetGap - this.currentGap) * 0.18;
        this.setGap(this.currentGap);
      } else {
        this.setGap(targetGap);
      }

      if (this.progressBar) {
        this.progressBar.style.width = `${progress * 100}%`;
      }

      if (this.slideIndex > 0 && !this.isContinuousMode) {
        this.updateSlide(false);
      }
    }

    setGap(value) {
      this.track.style.setProperty('--csss-card-gap', `${value.toFixed(2)}px`);
    }

    slide(direction) {
      if (this.isContinuousMode || !this.cards.length) return;

      const maxIndex = this.getMaxSlideIndex();
      if (maxIndex <= 0) {
        this.slideIndex = 0;
      } else {
        let nextIndex = this.slideIndex + direction;
        if (nextIndex > maxIndex) nextIndex = 0;
        if (nextIndex < 0) nextIndex = maxIndex;
        this.slideIndex = nextIndex;
      }

      if (this.isMobileExpandingMode()) {
        this.displayIndex = this.slideIndex;
        this.applyMobileOffset();
        this.updateNavState();
        return;
      }

      this.updateSlide(true);
      this.updateNavState();
    }

    getVisibleCount() {
      if (this.isMobileExpandingMode()) return 1;

      const width = window.innerWidth;
      let perView = this.perView;
      if (width <= 767) {
        perView = this.perViewMobile;
      } else if (width <= 1024) {
        perView = this.perViewTablet;
      }
      return Math.min(perView, this.cards.length || 1);
    }

    updateSlide(animate) {
      if (!this.cards.length || this.isContinuousMode) return;

      if (this.isMobileExpandingMode()) {
        this.applyMobileOffset();
        return;
      }

      const card = this.cards[0];
      const gap = parseFloat(getComputedStyle(this.track).getPropertyValue('--csss-card-gap')) || this.maxGap;
      const cardWidth = card.getBoundingClientRect().width;
      const offset = -(this.slideIndex * (cardWidth + gap));

      this.track.style.transition = animate
        ? 'transform 0.85s cubic-bezier(0.22, 1, 0.36, 1)'
        : 'none';
      this.track.style.setProperty('--csss-slide-offset', `${offset}px`);
    }

    updateNavState() {
      if (!this.prevBtn || !this.nextBtn || this.isContinuousMode) return;
      this.prevBtn.disabled = false;
      this.nextBtn.disabled = false;
    }
  }

  function initCollectionScrollSliders() {
    document.querySelectorAll('[data-csss-slider]').forEach((root) => {
      if (root.dataset.csssInitialized === 'true') return;
      root.dataset.csssInitialized = 'true';
      new CollectionScrollSlider(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initCollectionScrollSliders);
  } else {
    initCollectionScrollSliders();
  }

  document.addEventListener('shopify:section:load', initCollectionScrollSliders);
})();
